import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { WindowService } from 'src/shared/providers/window.service';
import { Util } from 'src/shared/util/util';

import { FeatureGateService } from 'src/shared/services/feature-gate.service';

import { ScreeningDetailPage } from 'src/pages/leasing/screening-detail/screening-detail';
import { LeaseLedgerPage } from 'src/pages/leases/lease-ledger/lease-ledger';

import {
  TenantScreeningService,
  TenantScreening,
  ScreeningRecommendation,
  ScreeningStatus,
} from 'src/shared/services/tenant-screening.service';
import { LeaseService, Lease, LeaseStatus } from 'src/shared/services/lease.service';
import { TenantPortalService } from 'src/shared/services/tenant-portal.service';

interface GuestContactDetail {
  name: string;
  email: string;
  phone: string;
  properties: string[];
  stays: number;
  source: string;
}

@Component({
  templateUrl: 'guest-detail.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class GuestDetailPage implements OnInit {
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private navCtrl = inject(CustomNavController);
  private rightSidePanelService = inject(RightSidePanelService);
  private storage = inject(TidyStorage);
  private util = inject(Util);
  private featureGate = inject(FeatureGateService);
  private screeningService = inject(TenantScreeningService);
  private leaseService = inject(LeaseService);
  private portalService = inject(TenantPortalService);

  readonly windowService = inject(WindowService);

  isRightSideContent = true;
  contact: GuestContactDetail;
  screening: TenantScreening | undefined;
  leases: Lease[] = [];
  loaded = false;
  isOrdering = false;
  // Lease id -> cadence-aware rent display ("$225/wk" for weekly room leases,
  // "$2,450/mo" otherwise). Precomputed on load — no functions in templates.
  rentDisplayByLeaseId = new Map<number, string>();
  private panelClosedSubscription: Subscription;

  viewReportAction = () => this.viewReport();
  orderScreeningAction = () => this.orderScreening();
  copyPortalAction = () => this.copyPortalLink();
  sendPortalAction = () => this.sendPortalLink();

  async ngOnInit() {
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    const params = await this.storage.retrieve('dialog-params');
    this.contact = this.navCtrl.getParam('contact') || params?.contact;
    if (this.contact) {
      [this.screening, this.leases] = await Promise.all([
        this.screeningService.getScreeningByEmail(this.contact.email),
        this.leaseService.getLeasesForTenant(this.contact.email),
      ]);
      this.rentDisplayByLeaseId = new Map(this.leases.map(lease => [lease.id, this.rentDisplay(lease)]));
    }
    if (this.isRightSideContent && this.contact) {
      this.rightSidePanelService.setDialogPageTitle(this.contact.name);
    }
    setTimeout(() => {
      this.loaded = true;
      this.cdr.markForCheck();
    });
  }

  get propertiesLabel(): string {
    return (this.contact?.properties || []).filter(Boolean).join(', ') || '—';
  }

  get portalLink(): string {
    return this.contact ? this.portalService.portalLink(this.contact.email || this.contact.name) : '';
  }

  copyPortalLink(): void {
    try { navigator?.clipboard?.writeText(this.portalLink); } catch { /* clipboard unavailable */ }
    this.util.showSuccess(`Tenant portal link copied: ${  this.portalLink}`);
  }

  sendPortalLink(): void {
    this.featureGate.gate();
  }

  recommendationLabel(rec: ScreeningRecommendation): string { return this.screeningService.recommendationLabel(rec); }
  recommendationColor(rec: ScreeningRecommendation): string { return this.screeningService.recommendationColor(rec); }
  statusColor(status: ScreeningStatus): string { return this.screeningService.statusColor(status); }
  creditBand(score: number | null): string { return this.screeningService.creditBand(score); }

  private rentDisplay(lease: Lease): string {
    if (lease.cadence === 'weekly' && lease.weekly_rent) {
      return `$${  lease.weekly_rent.toLocaleString('en-US')  }/wk`;
    }
    return `$${  lease.monthly_rent.toLocaleString('en-US')  }/mo`;
  }

  leaseBalance(lease: Lease): number { return this.leaseService.balanceFor(lease); }
  balanceColor(balance: number): string { return this.leaseService.balanceColor(balance); }
  balanceLabel(balance: number): string { return this.leaseService.balanceLabel(balance); }
  leaseStatusColor(status: LeaseStatus): string { return this.leaseService.statusColor(status); }

  viewLedger(lease: Lease): void {
    this.goToPage('lease-ledger', { lease }, LeaseLedgerPage);
  }

  viewReport(): void {
    if (this.screening) {
      this.goToPage('screening-detail', { screening: this.screening }, ScreeningDetailPage);
    }
  }

  async orderScreening(): Promise<void> {
    this.featureGate.gate();
  }

  async goToPage(url: string, params: any, component: any) {
    const isMobileResolution = !this.windowService.isDesktopRes;
    if (!isMobileResolution) {
      const isRightSidePanelAlreadyOpen = await this.storage.retrieve('dialog-right-side-open');
      if (isRightSidePanelAlreadyOpen) {
        this.rightSidePanelService.navigateTo(url, params, component);
      } else {
        this.storage.save('dialog-right-side-open', true);
        this.storage.save('dialog-params', params);
        this.rightSidePanelService.openRightSidePanel(component);
      }
      this.panelClosedSubscription?.unsubscribe();
      this.panelClosedSubscription = this.rightSidePanelService.panelClosed().pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => this.cdr.markForCheck());
    } else {
      this.navCtrl.navigateForward(url, params);
    }
  }
}
